import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { FilePdf, FunnelSimple, ChartBar } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { downloadBlob } from '../utils/download';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceReport = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      const r = await axios.get(`${API_URL}/api/reports/services?${params}`, { withCredentials: true });
      setReport(r.data);
    } catch (e) {
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      const r = await axios.get(`${API_URL}/api/reports/services/pdf?${params}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      downloadBlob(r.data, 'rapport_interventions.pdf', 'application/pdf');
      toast.success('Rapport téléchargé');
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="service-report-page">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Rapport interventions</h1>
            <p className="text-slate-600">Analysez les interventions à domicile sur une période donnée</p>
          </div>
          <Button
            onClick={downloadPdf}
            data-testid="download-services-report-pdf"
            className="bg-red-600 hover:bg-red-500 text-white"
          >
            <FilePdf size={20} className="mr-2" />
            Export PDF
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Date début</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="report-start-date"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Date fin</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="report-end-date"
              />
            </div>
            <Button
              onClick={fetchReport}
              data-testid="apply-report-filters"
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              <FunnelSimple size={20} className="mr-2" />
              Appliquer
            </Button>
            <Button
              onClick={() => { setStartDate(''); setEndDate(''); setTimeout(fetchReport, 0); }}
              data-testid="reset-report-filters"
              variant="outline"
            >
              Réinitialiser
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : report ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Factures concernées</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="report-invoices-count">{report.invoices_count}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Quantité interventions</p>
                <p className="text-3xl font-bold text-blue-600" data-testid="report-total-qty">
                  {Number(report.total_quantity || 0).toFixed(0)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wider text-orange-700 mb-1">Chiffre d&apos;affaires interventions (TTC)</p>
                <p className="text-3xl font-bold text-orange-600" data-testid="report-total-amount">
                  {Number(report.total_amount || 0).toFixed(2)} €
                </p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                <ChartBar size={18} className="text-blue-600" />
                <h2 className="font-semibold text-slate-900">Détail par intervention</h2>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Type</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Description</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Qté totale</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Lignes</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Total TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {report.services && report.services.length > 0 ? report.services.map((s, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-600">{s.type}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{s.description}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right">{Number(s.quantity).toFixed(0)}</td>
                      <td className="py-3 px-4 text-sm text-slate-600 text-right">{s.invoice_count}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right font-bold">{s.total_amount.toFixed(2)} €</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="py-12 text-center text-slate-500">Aucune intervention sur la période</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {report.details && report.details.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mt-6">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                  <ChartBar size={18} className="text-blue-600" />
                  <h2 className="font-semibold text-slate-900">Détail par facture</h2>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">N° Facture</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Client</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Description</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Qté</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Total TTC</th>
                      <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-500">État</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.details.map((d, idx) => {
                      const isCancelled = d.status === 'cancelled';
                      return (
                        <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50 ${isCancelled ? 'bg-red-50 text-slate-500' : ''}`} data-testid={`report-detail-row-${idx}`}>
                          <td className="py-2 px-4 text-sm">{d.date ? new Date(d.date).toLocaleDateString('fr-FR') : '-'}</td>
                          <td className={`py-2 px-4 text-sm font-medium ${isCancelled ? 'line-through text-slate-500' : 'text-blue-700'}`}>{d.invoice_number}</td>
                          <td className="py-2 px-4 text-sm">{d.client_name}</td>
                          <td className={`py-2 px-4 text-sm ${isCancelled ? 'line-through' : 'text-slate-600'}`}>{d.description}</td>
                          <td className="py-2 px-4 text-sm text-right">{Number(d.quantity).toFixed(0)}</td>
                          <td className={`py-2 px-4 text-sm text-right font-medium ${isCancelled ? 'line-through' : ''}`}>{d.total_price.toFixed(2)} €</td>
                          <td className="py-2 px-4 text-center">
                            {isCancelled ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Annulée</span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Active</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ServiceReport;
